import React from 'react';
import Chip, { ChipProps } from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import { statusChipColors, colors } from '../../theme/tokens';

type StatusVariant = keyof typeof statusChipColors;

interface StatusChipProps {
  status: StatusVariant | string;
  label?: string;
  size?: ChipProps['size'];
  icon?: ChipProps['icon'];
  /**
   * WU12 — when provided, wrap the chip in a Tooltip. Used by the
   * promotion pipeline to surface who/when/duration on hover.
   * MUI Tooltip needs a non-disabled child ref target, so we
   * wrap the Chip in a <span> for compatibility.
   */
  tooltipContent?: React.ReactNode;
}

export default function StatusChip({
  status,
  label,
  size = 'small',
  icon,
  tooltipContent,
}: StatusChipProps) {
  const key = status.toLowerCase().replace(/\s+/g, '_') as StatusVariant;
  const palette = statusChipColors[key] || {
    bg: colors.bgMuted,
    color: colors.textSecondary,
  };

  // WU12 visual extensions for new status keys:
  // - rolled_back: strikethrough text
  // - unknown: transparent bg with border-color hint instead of solid fill
  const isRolledBack = key === ('rolled_back' as StatusVariant);
  const isUnknown = key === ('unknown' as StatusVariant);

  const chip = (
    <Chip
      label={label || status}
      size={size}
      icon={icon}
      sx={{
        fontWeight: 600,
        fontSize: 12,
        fontFamily: 'Manrope, sans-serif',
        backgroundColor: isUnknown ? 'transparent' : palette.bg,
        color: palette.color,
        border: isUnknown ? `1px solid ${palette.color}` : 'none',
        borderRadius: '6px',
        ...(isRolledBack && { textDecoration: 'line-through' }),
      }}
    />
  );

  if (tooltipContent) {
    return (
      <Tooltip title={tooltipContent} arrow placement="top">
        <span>{chip}</span>
      </Tooltip>
    );
  }
  return chip;
}
